import { Link, useLocation, useNavigate } from "react-router-dom";
import type { ReactNode } from "react";

import { Logo } from "@/components/logo";
import { ADMIN_APP_NAME } from "@/lib/constants";
import { useAdminAuth } from "@/lib/auth/auth-context";

const NAV = [
  { href: "/dashboard", label: "ภาพรวม" },
  { href: "/users", label: "ผู้ใช้งาน" },
  { href: "/stores", label: "ร้านค้า" },
  { href: "/products", label: "สินค้า" },
  { href: "/bookings", label: "การจอง" },
  { href: "/reports", label: "ข้อพิพาท" },
  { href: "/plans", label: "แพ็กเกจสมาชิก" },
  { href: "/credits", label: "เครดิต" },
  { href: "/promotions", label: "Boost / โปรโมชัน" },
  { href: "/content", label: "เนื้อหา (CMS)" },
  { href: "/audit-logs", label: "Audit Logs" },
  { href: "/webhooks", label: "Webhooks" },
];

const TITLE_BY_PATH: Record<string, string> = {
  "/dashboard": "ภาพรวมแพลตฟอร์ม",
  "/users": "จัดการผู้ใช้งาน",
  "/stores": "จัดการร้านค้า",
  "/products": "ตรวจสอบสินค้า",
  "/bookings": "จัดการการจอง",
  "/reports": "จัดการข้อพิพาท",
  "/plans": "แพ็กเกจสมาชิก",
  "/credits": "เครดิต",
  "/promotions": "Boost และโปรโมชัน",
  "/content": "จัดการเนื้อหา (CMS)",
  "/audit-logs": "Audit Logs",
  "/webhooks": "Webhooks",
};

export function AdminShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout } = useAdminAuth();

  const activeItem = NAV.find(
    (item) => location.pathname === item.href || location.pathname.startsWith(`${item.href}/`),
  );
  const pageTitle = activeItem ? (TITLE_BY_PATH[activeItem.href] ?? activeItem.label) : ADMIN_APP_NAME;

  return (
    <div className="grid min-h-screen grid-cols-1 bg-slate-bg sm:grid-cols-[224px_1fr]">
      <aside className="flex flex-col gap-0.5 bg-slate-sidebar px-3 py-4.5">
        <Link to="/dashboard" className="mb-1 flex items-center gap-2 px-2.5 pt-1.5 pb-4.5">
          <Logo iconSize={24} textClassName="text-[15px]" />
          <span className="text-[10.5px] font-medium text-white/50">Admin</span>
        </Link>
        {NAV.map((item) => {
          const active = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
          return (
            <Link
              key={item.href}
              to={item.href}
              className={`rounded-lg px-3 py-2 text-[13px] font-semibold ${
                active ? "bg-plum text-white" : "text-white/65 hover:bg-white/10"
              }`}
            >
              {item.label}
            </Link>
          );
        })}
      </aside>

      <div>
        <header className="flex items-center justify-between border-b border-slate-line bg-white px-6.5 py-3.5">
          <p className="font-heading text-[16px] font-semibold text-ink">{pageTitle}</p>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-[12.5px] font-semibold text-ink">{admin?.displayName ?? admin?.email}</p>
              <p className="text-[11px] text-muted">{admin?.roles.join(", ")}</p>
            </div>
            <button
              type="button"
              onClick={() => {
                void logout().then(() => navigate("/login"));
              }}
              className="rounded-lg border border-slate-line px-3 py-1.5 text-[12.5px] font-semibold text-ink hover:bg-slate-bg"
            >
              ออกจากระบบ
            </button>
          </div>
        </header>
        <main className="p-6.5">{children}</main>
      </div>
    </div>
  );
}
